/**
 * Tour De Cure published breakthroughs by year and cancer category.
 * Aggregated from TDC Breakthrough Summary (July 2026).
 * 280 dated records; one additional row has no publication year and is omitted.
 *
 * Raw "Cancer type/domain" values (120+ distinct strings) were simplified
 * into 15 generic categories for the chart filter.
 */
export type BreakthroughYearCount = {
  year: number;
  count: number;
};

export type BreakthroughCategory = {
  /** Display label shown in the filter dropdown and tooltip. */
  label: string;
  /** Per-year counts, indexed to BREAKTHROUGH_YEAR_START..BREAKTHROUGH_YEAR_END. */
  counts: readonly number[];
};

export const BREAKTHROUGH_YEAR_START = 2010;
export const BREAKTHROUGH_YEAR_END = 2026;

const COUNTS_BY_YEAR: Readonly<Record<number, number>> = {
  2010: 2,
  2012: 2,
  2013: 10,
  2014: 5,
  2015: 3,
  2016: 1,
  2017: 4,
  2018: 14,
  2019: 9,
  2020: 22,
  2021: 37,
  2022: 30,
  2023: 24,
  2024: 51,
  2025: 43,
  2026: 23,
};

export const tdcBreakthroughsByYear: BreakthroughYearCount[] = Array.from(
  { length: BREAKTHROUGH_YEAR_END - BREAKTHROUGH_YEAR_START + 1 },
  (_, index) => {
    const year = BREAKTHROUGH_YEAR_START + index;
    return { year, count: COUNTS_BY_YEAR[year] ?? 0 };
  },
);

export const tdcBreakthroughTotal = tdcBreakthroughsByYear.reduce(
  (sum, point) => sum + point.count,
  0,
);

export const tdcBreakthroughPeak: BreakthroughYearCount = tdcBreakthroughsByYear.reduce<BreakthroughYearCount>(
  (peak, point) => (point.count > peak.count ? point : peak),
  { year: BREAKTHROUGH_YEAR_START, count: 0 },
);

/* ── Cancer-type category data ────────────────────────────────────────── */

const YEARS = Array.from(
  { length: BREAKTHROUGH_YEAR_END - BREAKTHROUGH_YEAR_START + 1 },
  (_, i) => BREAKTHROUGH_YEAR_START + i,
);

function buildCategory(label: string, counts: readonly number[]): BreakthroughCategory {
  return { label, counts };
}

/** Ordered by total breakthroughs (descending). "All Cancers" always first. */
export const tdcBreakthroughCategories: BreakthroughCategory[] = [
  buildCategory("All Cancers", [2, 0, 2, 2, 3, 2, 0, 1, 1, 1, 4, 8, 7, 5, 16, 12, 11]),
  buildCategory("Brain Cancer", [0, 0, 0, 1, 0, 0, 0, 0, 3, 0, 3, 5, 6, 5, 5, 3, 2]),
  buildCategory("Pancreatic Cancer", [0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 4, 9, 4, 0, 0, 5, 1]),
  buildCategory("Breast Cancer", [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 2, 2, 8, 5, 4]),
  buildCategory("Bowel & GI", [0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 3, 2, 2, 3, 4, 4, 2]),
  buildCategory("Melanoma", [0, 0, 0, 0, 1, 0, 0, 1, 3, 2, 2, 2, 3, 1, 2, 2, 2]),
  buildCategory("Leukaemia & Blood", [0, 0, 0, 2, 0, 0, 1, 1, 2, 0, 1, 2, 0, 2, 4, 2, 0]),
  buildCategory("Bone & Sarcoma", [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 2, 1, 2, 4, 0]),
  buildCategory("Childhood Cancer", [0, 0, 0, 1, 0, 1, 0, 1, 2, 4, 1, 1, 0, 0, 0, 0, 0]),
  buildCategory("Neuroblastoma", [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 2, 2, 2, 1, 1, 1, 0]),
  buildCategory("Lung Cancer", [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 1, 4, 2, 0]),
  buildCategory("Prostate Cancer", [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 1, 2, 1, 0]),
  buildCategory("Ovarian Cancer", [0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 2, 1]),
  buildCategory("Liver Cancer", [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 0, 0]),
  buildCategory("Lymphoma", [0, 0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0]),
];

/** Special value meaning "show all categories combined" (the original totals). */
export const ALL_CATEGORIES_FILTER = "__all__";

/** Build year-count pairs for a given category label (or all combined when null). */
export function getBreakthroughDataForCategory(
  categoryLabel: string | null,
): BreakthroughYearCount[] {
  if (!categoryLabel) {
    return tdcBreakthroughsByYear;
  }
  const cat = tdcBreakthroughCategories.find((c) => c.label === categoryLabel);
  if (!cat) {
    return tdcBreakthroughsByYear;
  }
  return YEARS.map((year, i) => ({ year, count: cat.counts[i] ?? 0 }));
}

/** Total breakthroughs for a single category. */
export function getCategoryTotal(categoryLabel: string): number {
  const cat = tdcBreakthroughCategories.find((c) => c.label === categoryLabel);
  if (!cat) return 0;
  return cat.counts.reduce((s, c) => s + c, 0);
}

/** One chart row per year with a numeric field per category (stacked charts). */
export type BreakthroughStackedPoint = { year: number } & Record<string, number>;

/** Year rows keyed by category label; each row sums to that year's total. */
export function getBreakthroughStackedData(): BreakthroughStackedPoint[] {
  return YEARS.map((year, i) => {
    const row: Record<string, number> = { year };
    for (const cat of tdcBreakthroughCategories) {
      row[cat.label] = cat.counts[i] ?? 0;
    }
    return row as BreakthroughStackedPoint;
  });
}

/** Highlight colour per category for the stacked chart, legend, and tooltip. */
export const tdcBreakthroughCategoryColors: Readonly<Record<string, string>> = {
  "All Cancers": "#0A7A93",
  "Brain Cancer": "#E4572E",
  "Pancreatic Cancer": "#6D28D9",
  "Breast Cancer": "#D946A0",
  "Bowel & GI": "#B45309",
  "Melanoma": "#334155",
  "Leukaemia & Blood": "#DC2626",
  "Bone & Sarcoma": "#059669",
  "Childhood Cancer": "#D9A400",
  "Neuroblastoma": "#A78BFA",
  "Lung Cancer": "#0891B2",
  "Prostate Cancer": "#64748B",
  "Ovarian Cancer": "#F97316",
  "Liver Cancer": "#84CC16",
  "Lymphoma": "#14B8A6",
};
